'use strict';
const toast = require('../utils/genericToast');

const MINICART_CHANGE_DEBOUNCE_MS = 120;
const pendingQtyByUuid = new Map();
const minicartChangeTimers = new Map();

/**
 * Quantity stepper UI for select or number inputs inside `.product-qty-stepper` / `.product-tile-qty-wrap`.
 */

/**
 * On PLP tiles, qty + must stay locked while Add to Cart is disabled.
 * @param {JQuery} $field - Quantity select/input
 * @returns {boolean}
 */
function isPlpTileAddToCartDisabled($field) {
	const $shell = $field.closest('.product-tile-shell');
	if (!$shell.length) {
		return false;
	}
	const $atc = $shell.find('.product-tile__add-to-cart').first();
	return $atc.length > 0 && (!!$atc.prop('disabled') || $atc.hasClass('disabled'));
}

/**
 * Highest purchasable quantity already shown on the plus button.
 * A later response must not lower it: live ATS can shrink by the units on this line
 * and would disable plus one step below the real stock.
 * @param {JQuery} $plus - Plus button
 * @param {number|string} reported - Newly reported stock cap, if any
 * @returns {number}
 */
function stockCapOf($plus, reported) {
	let stored = parseInt($plus.attr('data-remaining'), 10);
	let incoming = parseInt(reported, 10);
	if (!isNaN(incoming) && incoming > 0 && (isNaN(stored) || incoming > stored)) {
		stored = incoming;
		$plus.attr('data-remaining', stored);
	}
	return stored;
}

/**
 * Plus stays enabled at the stock cap so the click can show the scarcity toast.
 * The option list often ends on that same cap, so end-of-list must not disable it either.
 * It is disabled when PLP add-to-cart is locked, or when there is no stock cap and the quantity list is exhausted.
 * @param {JQuery} $plus - Plus button
 * @param {number} currentQty - Selected quantity
 * @param {boolean} atEndOfList - No higher option exists
 * @param {boolean} lockPlus - PLP add-to-cart lock
 */
function applyPlusState($plus, currentQty, atEndOfList, lockPlus) {
	let stockCap = stockCapOf($plus);
	let hasStockCap = !isNaN(stockCap);
	let reachedStock = hasStockCap && !isNaN(currentQty) && currentQty >= stockCap;
	let disable = !reachedStock && !!(lockPlus || (atEndOfList && !hasStockCap));
	$plus.prop('disabled', disable);
	if (!disable) {
		$plus.removeAttr('disabled');
	}
	if (hasStockCap && !isNaN(currentQty) && currentQty < stockCap) {
		$plus.closest('.cart-line-item').find('.cart-line-item__footer-container').removeClass('not-available');
	}
}

/**
 * @param {JQuery} $plus - Plus button carrying the limit message
 */
function showQuantityLimitToast($plus) {
	const message = $plus.data('error-message');
	if (!message) {
		return;
	}
	toast.show({
		message: message,
		iconClass: 'icon-exclamation-triangle',
		error: true
	});
}

/**
 * Plus is also locked when the page flags a limit of its own via `data-qty-locked="true"`.
 * @param {JQuery} $field - Quantity select/input
 */
function syncQtyStepperUI($field) {
	const $wrap = findQtyStepperRoot($field);
	if (!$wrap.length) {
		return;
	}
	const qty = $field.val();
	if (qty != null && qty !== '') {
		const $mcpRoot = $field.closest('.product-tile-shell, .product-detail, .cart-line-item');
		$mcpRoot.attr('data-qty', qty);
		$mcpRoot.find('[data-mcp-atc="true"], button.add-to-cart, .product-tile__add-to-cart, article.product-tile').attr('data-qty', qty);
	}
	const $plus = $wrap.find('.product-qty-plus');
	const lockTile = isPlpTileAddToCartDisabled($field);
	const lockPlus = lockTile || $plus.attr('data-qty-locked') === 'true';
	if ($field.is('select')) {
		const $opt = $field.find('option:selected');
		const displayVal = $opt.length ? $opt.val() || $.trim($opt.text()) : '';
		$wrap.find('.product-qty-display').text(displayVal);
		const idx = $field.prop('selectedIndex');
		const maxIdx = $field.find('option').length - 1;
		const currentValue = parseInt($opt.val(), 10);
		$wrap.find('.product-qty-minus').prop('disabled', idx <= 0 || lockTile);
		applyPlusState($plus, currentValue, maxIdx < 0 || idx >= maxIdx, lockPlus);
		return;
	}
	let min = parseInt($field.attr('min'), 10);
	let max = parseInt($field.attr('max'), 10);
	if (isNaN(min)) {
		min = 1;
	}
	if (isNaN(max)) {
		max = 99;
	}
	let n = parseInt($field.val(), 10);
	if (isNaN(n) || n < min) {
		n = min;
		$field.val(n);
	}
	if (n > max) {
		n = max;
		$field.val(n);
	}
	$wrap.find('.product-qty-display').text(String(n));
	$wrap.find('.product-qty-minus').prop('disabled', n <= min || lockTile);
	applyPlusState($plus, n, n >= max, lockPlus);
}

/**
 * @param {string|*} uuid - Line item UUID
 * @returns {string}
 */
function normalizeUuid(uuid) {
	return uuid == null ? '' : String(uuid);
}

/**
 * @param {string|*} uuid - Line item UUID
 * @param {number} qty - Pending quantity
 */
function setPendingQtyByUuid(uuid, qty) {
	const key = normalizeUuid(uuid);
	if (!key || isNaN(qty)) {
		return;
	}
	pendingQtyByUuid.set(key, qty);
}

/**
 * @param {string|*} uuid - Line item UUID
 * @returns {number|null}
 */
function getPendingQtyByUuid(uuid) {
	const key = normalizeUuid(uuid);
	if (!key || !pendingQtyByUuid.has(key)) {
		return null;
	}
	return pendingQtyByUuid.get(key);
}

/**
 * @param {string|*} uuid - Line item UUID
 */
function clearPendingQtyByUuid(uuid) {
	const key = normalizeUuid(uuid);
	if (key) {
		pendingQtyByUuid.delete(key);
	}
}

/**
 * @returns {boolean}
 */
function hasPendingQuantities() {
	return pendingQtyByUuid.size > 0;
}

/**
 * @returns {Object.<string, number>}
 */
function getPendingQuantities() {
	const quantities = {};
	pendingQtyByUuid.forEach(function (qty, uuid) {
		quantities[uuid] = qty;
	});
	return quantities;
}

/**
 * True while a minicart qty change is debounced and not yet sent.
 * @returns {boolean}
 */
function isQtyUpdatePending() {
	return minicartChangeTimers.size > 0;
}

/**
 * @param {JQuery} $field - Quantity field
 */
function setPendingFromField($field) {
	const pendingQty = parseInt($field.val(), 10);
	if (!isNaN(pendingQty)) {
		setPendingQtyByUuid($field.data('uuid'), pendingQty);
	}
}

/**
 * @param {JQuery} $field - Quantity field
 * @returns {boolean}
 */
function isInMinicart($field) {
	return $field.closest('.minicart').length > 0;
}

/**
 * Triggers quantity change immediately, or debounced when inside the minicart
 * so rapid +/- clicks coalesce into a single UpdateQuantity.
 * @param {JQuery} $field - Quantity field
 */
function triggerQuantityChange($field) {
	if (!isInMinicart($field)) {
		$field.trigger('change');
		return;
	}

	const uuid = normalizeUuid($field.data('uuid'));
	if (!uuid) {
		$field.trigger('change');
		return;
	}

	if (minicartChangeTimers.has(uuid)) {
		clearTimeout(minicartChangeTimers.get(uuid));
	}

	minicartChangeTimers.set(
		uuid,
		setTimeout(function () {
			minicartChangeTimers.delete(uuid);

			let $target = $('.minicart .quantity').filter(function () {
				return normalizeUuid($(this).data('uuid')) === uuid;
			}).first();

			if (!$target.length) {
				$target = $field;
			}

			const pending = getPendingQtyByUuid(uuid);
			if (pending !== null && parseInt($target.val(), 10) !== pending) {
				$target.val(String(pending));
				syncQtyStepperUI($target);
			}

			$target.trigger('change');
			$('body').trigger('minicart:qtyChangeFlushed', { uuid: uuid });
		}, MINICART_CHANGE_DEBOUNCE_MS)
	);
}

/**
 * Restores select/display to pending qty when SFRA overwrites with a stale response.
 * @param {string|*} uuid - Line item UUID
 */
function restorePendingQtyUi(uuid) {
	const pending = getPendingQtyByUuid(uuid);
	if (pending === null) {
		return;
	}

	const key = normalizeUuid(uuid);
	const $fields = $('.quantity').filter(function () {
		return normalizeUuid($(this).data('uuid')) === key;
	});

	$fields.each(function () {
		const $field = $(this);
		if (parseInt($field.val(), 10) !== pending) {
			$field.val(String(pending));
			syncQtyStepperUI($field);
		}
	});
}

function syncAllQtySteppers($root) {
	const $ctx = $root && $root.length ? $root : $(document);
	$ctx.find('.product-qty').each(function () {
		syncQtyStepperUI($(this));
	});
}

function findQtyStepperRoot($control) {
	let $root = $control.closest('.product-qty-wrap');
	if ($root.length) {
		return $root;
	}
	// Cart line items: stepper and select are siblings inside .quantity-form
	$root = $control.closest('.quantity-form');
	if ($root.length) {
		return $root;
	}
	const $stepper = $control.closest('.product-qty-stepper');
	if ($stepper.length) {
		return $stepper.parent();
	}
	return $();
}

function findQtyField($control) {
	return findQtyStepperRoot($control).find('select.product-qty, input.product-qty');
}

function getTileShell($context) {
	const $shell = $context.closest('.product-tile-shell');
	if ($shell.length) {
		return $shell;
	}
	return $context.filter('.product-tile-shell');
}

function getTileVariationReferenceUrls($context) {
	const $shell = getTileShell($context);
	if (!$shell.length) {
		return [];
	}

	const urls = [];
	const storedUrl = $shell.attr('data-last-variation-url');
	if (storedUrl) {
		urls.push(storedUrl);
	}

	$shell.find('.product-tile__swatch-btn').each(function () {
		if (!$(this).find('.swatch-value.selected').length) {
			return;
		}
		const swatchUrl = $(this).attr('data-url');
		if (swatchUrl) {
			urls.push(swatchUrl);
		}
	});

	$shell.find('select.product__variation-select').each(function () {
		const packUrl = $(this).val();
		if (packUrl) {
			urls.push(packUrl);
		}
	});

	return urls;
}

function copyDwvarParams(targetUrl, sourceUrl) {
	const target = new URL(targetUrl, window.location.href);
	const source = new URL(sourceUrl, window.location.href);

	source.searchParams.forEach(function (value, key) {
		if (key.startsWith('dwvar_')) {
			target.searchParams.set(key, value);
		}
	});

	return target.pathname + target.search + target.hash;
}

function mergeQuantityVariationUrl(quantityUrl, $context) {
	if (!quantityUrl) {
		return '';
	}

	let mergedUrl = quantityUrl;
	getTileVariationReferenceUrls($context).forEach(function (referenceUrl) {
		mergedUrl = copyDwvarParams(mergedUrl, referenceUrl);
	});

	return mergedUrl;
}

function updateQuantities(product, $root) {
	if (!product || !product.quantities || !product.quantities.length || !$root || !$root.length) {
		return;
	}
	const $qtySelects = $root.find('select.product-qty');
	if (!$qtySelects.length) {
		return;
	}
	$qtySelects.each(function () {
		const $qtySelect = $(this);
		const currentValue = $qtySelect.val();
		const hasCurrentValue = product.quantities.some(function (quantity) {
			return String(quantity.value) === String(currentValue);
		});
		const optionsHtml = product.quantities
			.map(function (quantity) {
				const isSelected = hasCurrentValue
					? String(quantity.value) === String(currentValue)
					: quantity.selected;
				const selected = isSelected ? ' selected' : '';
				const optionUrl =
					product.productType !== 'bundle' && quantity.url ? mergeQuantityVariationUrl(quantity.url, $root) : '';
				const dataUrl = optionUrl ? ' data-url="' + optionUrl + '"' : '';
				return '<option value="' + quantity.value + '"' + dataUrl + selected + '>' + quantity.value + '</option>';
			})
			.join('');
		$qtySelect.empty().html(optionsHtml);
		$qtySelect
			.closest('.product-qty-wrap')
			.find('.product-qty-plus')
			.attr('data-remaining', product.scarcity ? product.scarcity.remaining : '');
		syncQtyStepperUI($qtySelect);
	});
}

function $cartPage() {
	return $('.cart-page');
}

function hasCartLineItems() {
	return $cartPage().length > 0;
}

function getBasketFromCartUpdate(data) {
	if (!data || typeof data !== 'object') {
		return null;
	}
	return data.basket || data.cartModel || data;
}

function syncCartQtySteppers() {
	if (!hasCartLineItems()) {
		return;
	}
	syncAllQtySteppers($cartPage());
}

function syncCartSteppersFromServerItems(data) {
	if (!hasCartLineItems()) {
		return;
	}

	const basket = getBasketFromCartUpdate(data);
	const items = basket && basket.items;
	if (!Array.isArray(items)) {
		return;
	}

	items.forEach(function (item) {
		if (!item || !item.UUID || item.quantity == null) {
			return;
		}

		const $qty = $cartPage()
			.find('.quantity')
			.filter(function () {
				return normalizeUuid($(this).data('uuid')) === normalizeUuid(item.UUID);
			})
			.first();

		if (!$qty.length) {
			return;
		}

		const pendingQty = getPendingQtyByUuid(item.UUID);
		const serverQty = parseInt(item.quantity, 10);

		if (pendingQty !== null && !isNaN(serverQty) && pendingQty !== serverQty) {
			return;
		}

		const $plus = $qty.closest('.quantity-form, .product-qty-wrap').find('.product-qty-plus');
		if (item.scarcity && item.scarcity.remaining != null) {
			stockCapOf($plus, item.scarcity.remaining);
		}

		$qty.val(String(item.quantity));
		$qty.data('pre-select-qty', item.quantity);
		clearPendingQtyByUuid(item.UUID);
		syncQtyStepperUI($qty);
	});
}

let stepperHandlersBound = false;
let cartAjaxSyncBound = false;

function bindCartAjaxSync() {
	if (cartAjaxSyncBound) {
		return;
	}
	cartAjaxSyncBound = true;

	$('body').on('cart:update.productQuantityStepper', function (_e, data) {
		syncCartSteppersFromServerItems(data);
	});

	// base/cart/cart reverts quantity on error with .val() and does not fire change.
	// On success it may also write a stale request qty; restore pending when needed.
	$(document).on('ajaxComplete.productQuantityStepper', function (_event, xhr, settings) {
		if (!settings || !settings.url || settings.url.indexOf('UpdateQuantity') === -1) {
			return;
		}

		const uuid = settings.context ? $(settings.context).data('uuid') : null;

		if (xhr.status >= 400) {
			if (uuid) {
				clearPendingQtyByUuid(uuid);
			}
			if (hasCartLineItems()) {
				syncCartQtySteppers();

				const $error = $('.cart-error .valid-cart-error').last();
				if ($error.length) {
					$error[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
				}
			}
			return;
		}

		if (uuid) {
			restorePendingQtyUi(uuid);
		}
	});
}

function bindStepperHandlers() {
	if (stepperHandlersBound) {
		return;
	}
	stepperHandlersBound = true;

	$('body').on('search:updateDom', function () {
		syncAllQtySteppers();
	});

	$(document).on('click', '.product-qty-minus', function () {
		const $field = findQtyField($(this));
		if (!$field.length) {
			return;
		}
		if ($field.is('select')) {
			const idx = $field.prop('selectedIndex');
			if (idx > 0) {
				$field.prop('selectedIndex', idx - 1);
				setPendingFromField($field);
				syncQtyStepperUI($field);
				triggerQuantityChange($field);
			}
			return;
		}
		const min = parseInt($field.attr('min'), 10) || 1;
		const n = parseInt($field.val(), 10) || min;
		if (n > min) {
			$field.val(n - 1);
			syncQtyStepperUI($field);
		}
	});

	$(document).on('click', '.product-qty-plus', function () {
		const $plus = $(this);
		const $field = findQtyField($plus);
		if (!$field.length) {
			return;
		}
		if ($plus.prop('disabled')) {
			return;
		}
		if ($field.is('select')) {
			const idx = $field.prop('selectedIndex');
			const maxIdx = $field.find('option').length - 1;
			const currentValue = parseInt($field.val(), 10);
			const stockCap = stockCapOf($plus);
			if ((!isNaN(stockCap) && currentValue >= stockCap) || idx >= maxIdx) {
				showQuantityLimitToast($plus);
				return;
			}
			const beforeIncrease = $.Event('quantity:beforeIncrease');
			$field.trigger(beforeIncrease);
			if (beforeIncrease.isDefaultPrevented()) {
				return;
			}
			$field.prop('selectedIndex', idx + 1);
			setPendingFromField($field);
			syncQtyStepperUI($field);
			triggerQuantityChange($field);
			return;
		}
		const max = parseInt($field.attr('max'), 10) || 99;
		const n = parseInt($field.val(), 10) || 1;
		if (n < max) {
			$field.val(n + 1);
			syncQtyStepperUI($field);
		}
	});

	$(document).on('change', 'select.product-qty', function () {
		syncQtyStepperUI($(this));
	});

	$(document).on('change', 'input.product-qty', function () {
		syncQtyStepperUI($(this));
	});
}

module.exports = function initProductQuantityStepper() {
	bindStepperHandlers();
	bindCartAjaxSync();
	syncCartQtySteppers();
	syncAllQtySteppers();
};

module.exports.syncQtyStepperUI = syncQtyStepperUI;
module.exports.syncAllQtySteppers = syncAllQtySteppers;
module.exports.updateQuantities = updateQuantities;
module.exports.mergeQuantityVariationUrl = mergeQuantityVariationUrl;
module.exports.setPendingQtyByUuid = setPendingQtyByUuid;
module.exports.getPendingQtyByUuid = getPendingQtyByUuid;
module.exports.clearPendingQtyByUuid = clearPendingQtyByUuid;
module.exports.hasPendingQuantities = hasPendingQuantities;
module.exports.getPendingQuantities = getPendingQuantities;
module.exports.isQtyUpdatePending = isQtyUpdatePending;
